/** @jsx jsx */
import { jsx } from "theme-ui"
import { useTranslation } from "react-i18next"
import { useStaticQuery, graphql } from "gatsby"
import { Box } from "rebass"

import Link from "../../atoms/Link"
import List from "../../atoms/List"
import GridRow from "../../atoms/GridRow"
import PageWrap from "../../atoms/PageWrap"

import { getResolvedLocale, getDataForLocale } from "../../../utils/i18n"

import logoSrc from "../../../images/logo.svg"

const headingStyle = { fontSize: "body", my: 0 }

const Footer = () => {
  const { i18n } = useTranslation()
  const { sanityNavigation, sanitySiteSettings } = useStaticQuery(query)
  const resolvedLocale = getResolvedLocale(i18n)
  const data = getDataForLocale(sanityNavigation, resolvedLocale.config.code)

  return (
    <PageWrap
      as="footer"
      sx={{
        position: "relative",
        backgroundColor: "white",
        boxShadow: "0 -3px 10px 0px rgba(0, 0, 0, 0.04)",
        pt: 7,
        pb: 7,
        "a:hover": {
          color: "primary",
          textDecoration: "underline",
        },
      }}
    >
      <GridRow>
        <GridRow.Col gridSpan={[8, 7]}>
          <Link
            to=""
            sx={{
              display: "block",
              variant: "variants.header.nav",
              [i18n.dir() === "rtl" ? "ml" : "mr"]: "auto",
              mb: [5, 0],
            }}
          >
            <img
              src={logoSrc}
              width={113}
              height={56}
              sx={{
                display: "block",
              }}
            />
          </Link>
        </GridRow.Col>
        <GridRow.Col
          gridSpan={4}
          sx={{
            ul: {
              ...(i18n.dir() === "rtl" ? { mr: 0, pr: 0 } : { ml: 0, pl: 0 }),
            },
          }}
        >
          <h6 sx={headingStyle}> {data._rawLinked}</h6>
          <List sx={{ gridGap: [1, 1], mt: 1 }}>
            {data.footer.map(navItem => (
              <li key={navItem.url}>
                <Link to={navItem.url}>{navItem.label}</Link>
              </li>
            ))}
          </List>
        </GridRow.Col>
        <GridRow.Col
          gridSpan={4}
          sx={{
            ul: {
              ...(i18n.dir() === "rtl" ? { mr: 0, pr: 0 } : { ml: 0, pl: 0 }),
            },
          }}
        >
          <h6 sx={headingStyle}>{data._rawSocial}</h6>
          <List sx={{ gridGap: [1, 1], mt: 1 }}>
            {sanitySiteSettings.socialLinks.twitter && (
              <li>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://twitter.com/${sanitySiteSettings.socialLinks.twitter}`}
                >
                  Twitter
                </a>
              </li>
            )}
            {sanitySiteSettings.socialLinks.facebook && (
              <li>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://facebook.com/${sanitySiteSettings.socialLinks.facebook}`}
                >
                  Facebook
                </a>
              </li>
            )}
            {sanitySiteSettings.socialLinks.instagram && (
              <li>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://instagram.com/${sanitySiteSettings.socialLinks.instagram}`}
                >
                  Instagram
                </a>
              </li>
            )}
            {sanitySiteSettings.socialLinks.linkedin && (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://linkedin.com/${sanitySiteSettings.socialLinks.linkedin}`}
              >
                Linkedin
              </a>
            )}
          </List>
        </GridRow.Col>
        <GridRow.Col gridSpan={[8, 5]}>
          <Box>
            <h6 sx={headingStyle}>{data._rawContact}</h6>
          </Box>
          <p sx={{ mt: 1 }}>
            <a href={`mailto:${sanitySiteSettings.contact.email}`}>
              {sanitySiteSettings.contact.email}
            </a>
          </p>
        </GridRow.Col>
      </GridRow>
    </PageWrap>
  )
}

export default Footer

const query = graphql`
  query NavFooter {
    sanityNavigation {
      footer {
        url
        label {
          ...StringAllLanguages
        }
      }
      _rawLinked(resolveReferences: { maxDepth: 5 })
      _rawSocial(resolveReferences: { maxDepth: 5 })
      _rawContact(resolveReferences: { maxDepth: 5 })
    }
    sanitySiteSettings {
      socialLinks {
        facebook
        twitter
        instagram
        linkedin
      }
      contact {
        city
        postcode
        street
        email
      }
    }
  }
`
